/**
 * Single source of truth for the file types the web app knows about.
 *
 * Every file-type list in the frontend derives from FILE_TYPES below: upload
 * `accept` attributes, extension allowlists, MIME allowlists, and the
 * extension -> category lookup used by the file preview. Add a type once here.
 */

export enum FileCategory {
  IMAGE = 'image',
  PDF = 'pdf',
  DOCUMENT = 'document',
  DATA = 'data',
}
// Enum string values intentionally match the strings the FileViewer components
// already branch on ('image' | 'pdf' | 'document' | 'data').

interface FileTypeDef {
  /** Lowercase extension without the leading dot, e.g. 'tiff'. */
  ext: string;
  /** Canonical MIME type, e.g. 'image/tiff'. */
  mime: string;
  category: FileCategory;
}

const FILE_TYPES: readonly FileTypeDef[] = [
  { ext: 'jpg', mime: 'image/jpeg', category: FileCategory.IMAGE },
  { ext: 'jpeg', mime: 'image/jpeg', category: FileCategory.IMAGE },
  { ext: 'png', mime: 'image/png', category: FileCategory.IMAGE },
  { ext: 'gif', mime: 'image/gif', category: FileCategory.IMAGE },
  { ext: 'tiff', mime: 'image/tiff', category: FileCategory.IMAGE },
  { ext: 'tif', mime: 'image/tiff', category: FileCategory.IMAGE },
  { ext: 'pdf', mime: 'application/pdf', category: FileCategory.PDF },
  { ext: 'doc', mime: 'application/msword', category: FileCategory.DOCUMENT },
  {
    ext: 'docx',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    category: FileCategory.DOCUMENT,
  },
  {
    ext: 'xls',
    mime: 'application/vnd.ms-excel',
    category: FileCategory.DOCUMENT,
  },
  {
    ext: 'xlsx',
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    category: FileCategory.DOCUMENT,
  },
  {
    ext: 'ppt',
    mime: 'application/vnd.ms-powerpoint',
    category: FileCategory.DOCUMENT,
  },
  {
    ext: 'pptx',
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    category: FileCategory.DOCUMENT,
  },
  { ext: 'csv', mime: 'text/csv', category: FileCategory.DOCUMENT },
  { ext: 'zip', mime: 'application/zip', category: FileCategory.DATA },
  { ext: 'rar', mime: 'application/vnd.rar', category: FileCategory.DATA },
  { ext: 'dat', mime: 'application/octet-stream', category: FileCategory.DATA },
];

/** `accept` attribute value for `<input type="file">`, e.g. ".jpg,.jpeg,.png,...". */
export const ACCEPT_ATTRIBUTE: string = FILE_TYPES.map((t) => `.${t.ext}`).join(
  ','
);

/** Allowed extensions WITH the leading dot, e.g. [".jpg", ".jpeg", ...]. */
export const ALLOWED_EXTENSIONS: string[] = FILE_TYPES.map((t) => `.${t.ext}`);

/** Allowed MIME types, de-duplicated. */
export const ALLOWED_MIME_TYPES: string[] = [
  ...new Set(FILE_TYPES.map((t) => t.mime)),
];

/**
 * Maps a file name to its preview category. Unknown or missing extensions fall
 * back to DOCUMENT (the previous default of the duplicated getDocumentType
 * helpers).
 */
export const getDocumentType = (fileName: string): FileCategory => {
  const ext = (fileName.split('.').pop() ?? '').toLowerCase();
  return (
    FILE_TYPES.find((t) => t.ext === ext)?.category ?? FileCategory.DOCUMENT
  );
};
